import { StateCreator } from 'zustand';

import { PlanningState, initialPlanningState } from '../../types/planning';

export interface PlanningSlice {
  // State
  planningState: PlanningState;

  // Actions
  updatePlanningField: <K extends keyof PlanningState>(field: K, value: PlanningState[K]) => void;
  updateMultiplePlanningFields: (updates: Partial<PlanningState>) => void;
  clearPlanningState: () => void;
  clearSceneDynamicFields: () => void;
  clearScenePlanningFields: () => void;
  clearBPlotFields: () => void;
  loadPlanningStateFromStorage: () => void;
}

export const createPlanningSlice: StateCreator<PlanningSlice, [], [], PlanningSlice> = (set) => ({
  // Initial state
  planningState: initialPlanningState,

  // Update a single field
  updatePlanningField: (field, value) =>
    set((state) => ({
      planningState: {
        ...state.planningState,
        [field]: value,
      },
      hasUnsavedChanges: true,
    })),

  // Update multiple fields at once
  updateMultiplePlanningFields: (updates) =>
    set((state) => ({
      planningState: {
        ...state.planningState,
        ...updates,
      },
      hasUnsavedChanges: true,
    })),

  // Clear all planning state
  clearPlanningState: () =>
    set(() => ({
      planningState: initialPlanningState,
      hasUnsavedChanges: true,
    })),

  // Clear only Scene Dynamic fields
  clearSceneDynamicFields: () =>
    set((state) => ({
      planningState: {
        ...state.planningState,
        dynamicType: '',
        realConversation: '',
        fictionalAnalysis: '',
      },
      hasUnsavedChanges: true,
    })),

  // Clear only Scene Planning fields
  clearScenePlanningFields: () =>
    set((state) => ({
      planningState: {
        ...state.planningState,
        playerRole: '',
        npcRole: '',
        playerName: '',
        npcName: '',
        npcMotivation: '',
        playerMotivation: '',
        aPlot: '',
        stakes: '',
        locationAtmosphere: '',
      },
      hasUnsavedChanges: true,
    })),

  // Clear only B-Plot fields
  clearBPlotFields: () =>
    set((state) => ({
      planningState: {
        ...state.planningState,
        bPlotSeed: '',
        characterNuanceNotes: '',
        bPlotReason: '',
        bPlotReasonOther: '',
        bPlotNature: '',
        bPlotNatureOther: '',
        bPlotExplanation: '',
      },
      hasUnsavedChanges: true,
    })),

  // Load planning state from localStorage
  loadPlanningStateFromStorage: () => {
    try {
      const stored = localStorage.getItem('planningState');
      if (stored) {
        const parsed = JSON.parse(stored);
        set(() => ({
          planningState: { ...initialPlanningState, ...parsed },
        }));
      }
    } catch (error) {
      console.error('Failed to load planning state from storage:', error);
    }
  },
});
